import gsap from 'gsap'
import { once } from 'ramda'
import type { PerspectiveCamera } from 'three'
import { Vector3 } from 'three'
import type CameraControls from 'camera-controls'
import type { CameraViewData } from '@/entities/model3d'

// Returns a function to cancel the tween
const tweenCamera = (
  camera: PerspectiveCamera,
  controls: CameraControls,
  destinationData: Partial<CameraViewData>,
  duration: number = 3,
  onUpdate: () => void = () => {},
  onComplete: () => void = () => {}
): (() => void) => {
  const { fov, position, target } = destinationData
  let fovTween: gsap.core.Tween | null = null
  const completeOnlyOnce = once(onComplete)

  if (fov) {
    fovTween = gsap.to(camera, {
      duration,
      fov: fov.x,
      onUpdate: () => {
        camera.updateProjectionMatrix()
        onUpdate()
      },
    })
  }

  let positionTween: gsap.core.Tween | null = null
  if (position) {
    const { x, y, z } = position
    let t = { t: 0 }
    const startingTarget = new Vector3()
    const startingPosition = new Vector3()
    controls.getTarget(startingTarget, false)
    controls.getPosition(startingPosition, false)
    const { x: tx0, y: ty0, z: tz0 } = startingTarget
    const { x: px0, y: py0, z: pz0 } = startingPosition
    const { x: tx1, y: ty1, z: tz1 } = target || startingTarget
    positionTween = gsap.to(t, {
      duration,
      t: 1,
      onUpdate: () => {
        controls.lerpLookAt(px0, py0, pz0, tx0, ty0, tz0, x, y, z, tx1, ty1, tz1, t.t, false)
      },
      onComplete: completeOnlyOnce,
      onInterrupt: completeOnlyOnce,
    })
  }

  return () => {
    fovTween?.kill()
    fovTween = null
    positionTween?.kill()
    positionTween = null
    completeOnlyOnce()
  }
}

export { tweenCamera }
